import { Slider as SliderPrimitive } from '@base-ui/react/slider';

import { cn } from '../lib/utils';

/* Genérico sobre `Value`, espelhando o `SliderRoot` do Base UI: sem o `<Value>` aqui, todo
   consumidor recebia o tipo mais largo (`number | readonly number[]`) no `onValueChange`,
   mesmo passando `value` como array de um elemento — o wrapper apagava a inferência que o
   primitivo já fazia sozinho. */
function Slider<Value extends number | readonly number[] = number | readonly number[]>({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  getAriaValueText,
  ...props
}: Readonly<
  SliderPrimitive.Root.Props<Value> & Pick<SliderPrimitive.Thumb.Props, 'getAriaValueText'>
>) {
  /**
   * Quantos polegares desenhar. **O `typeof === 'number'` é emenda nossa, e sem ele o slider de
   * valor único não funciona de jeito nenhum** — o wrapper do CLI erra nas duas pontas:
   *
   * - `value` número puro caía no `[min, max]` e desenhava DOIS polegares;
   * - `value` como array de um ligava o modo intervalo, porque o `SliderRoot` decide isso por
   *   `Array.isArray(value)` e não pelo comprimento. Em modo intervalo o `getFingerState` pula o
   *   atalho de thumb único, entra na resolução de colisão entre dois polegares e devolve `null` —
   *   o ponteiro morre inteiro: nem arrasto, nem clique no trilho.
   *
   * Então número é número (um polegar, `range` desligado no Base UI) e array é array.
   */
  let _values: readonly number[];
  if (Array.isArray(value)) {
    _values = value;
  } else if (Array.isArray(defaultValue)) {
    _values = defaultValue;
  } else if (typeof value === 'number') {
    _values = [value];
  } else if (typeof defaultValue === 'number') {
    _values = [defaultValue];
  } else {
    _values = [min, max];
  }

  return (
    <SliderPrimitive.Root
      className={cn('data-horizontal:w-full data-vertical:h-full', className)}
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      thumbAlignment="edge"
      {...props}
    >
      <SliderPrimitive.Control className="relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-vertical:h-full data-vertical:min-h-40 data-vertical:w-auto data-vertical:flex-col">
        <SliderPrimitive.Track
          data-slot="slider-track"
          className="relative grow overflow-hidden rounded-full bg-muted select-none data-horizontal:h-1 data-horizontal:w-full data-vertical:h-full data-vertical:w-1"
        >
          <SliderPrimitive.Indicator
            data-slot="slider-range"
            className="bg-primary select-none data-horizontal:h-full data-vertical:w-full"
          />
        </SliderPrimitive.Track>
        {Array.from({ length: _values.length }, (_, index) => (
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            key={index}
            getAriaValueText={getAriaValueText}
            // O nome da régua também no polegar: é nele (o `input` de
            // `role="slider"`) que o leitor de tela pousa, e o Base UI só o
            // nomeia por `Slider.Label`, que a gente não usa.
            aria-labelledby={props['aria-labelledby']}
            className="relative block size-3 shrink-0 rounded-full border border-ring bg-background ring-ring/50 transition-[color,box-shadow] select-none after:absolute after:-inset-2 hover:ring-3 focus-visible:ring-3 focus-visible:outline-hidden active:ring-3 disabled:pointer-events-none disabled:opacity-50"
          />
        ))}
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  );
}

export { Slider };
